import { useState } from "react";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { api } from "./api";
import { ImageChoice } from "./ImageChoice.jsx";

// TEST-020: internal Alias QA visual update — new version, no AI call, no client revision used.
export function VisualUpdate({ jobId, version, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [footer, setFooter] = useState(false);
  const [image, setImage] = useState(null);
  const ok = reason.trim().length >= 3;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.visualUpdate(jobId, version, reason.trim(), phone.trim(), footer, image);
      toast.success(`${r.version} created from ${version} — ready for Alias QA`);
      setOpen(false); setReason(""); setPhone(""); setFooter(false); setImage(null);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="visual-update-open"
        className="w-full flex items-center justify-center gap-2 rounded-2xl ring-1 ring-slate-700 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800/60 transition-colors">
        <Wand2 className="h-4 w-4" /> Advanced corrections (phone, photos, footer, layout)
      </button>
    );
  }
  return (
    <section className="rounded-2xl bg-slate-900/70 ring-1 ring-amber-500/40 p-4 space-y-3" data-testid="visual-update-panel">
      <p className="font-medium">Create a new draft from {version}</p>
      <p className="text-sm text-slate-400">
        Applies the latest approved layout and best-matching approved hero photo. The wording, business details,
        colours and other sections stay exactly as in {version} — this does not change any text. Optionally enter a
        verified corrected phone number to update it everywhere on the site. No AI is used, the client's revision is
        not used, and the client is not notified. {version} is kept unchanged.
      </p>
      <label className="block text-xs text-slate-400">
        Corrected phone number (optional)
        <input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} inputMode="tel"
          data-testid="visual-update-phone" placeholder="Leave blank to keep the current number"
          className="mt-1 w-full rounded-xl bg-slate-950 ring-1 ring-slate-700 p-3 text-sm text-slate-100 focus:outline-none focus:ring-amber-500" />
      </label>
      <ImageChoice jobId={jobId} value={image} onChange={setImage} />
      <label className="flex items-start gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={footer} onChange={(e) => setFooter(e.target.checked)}
          data-testid="visual-update-footer" className="mt-1 accent-amber-500" />
        <span>Apply the approved footer tagline rule
          <span className="block text-xs text-slate-500">Replaces only the footer tagline (e.g. "Bakery for homes across Dublin." becomes "Bakery in Dublin."). No other wording changes.</span>
        </span>
      </label>
      <label htmlFor="visual-update-reason" className="block text-xs text-slate-400 -mb-2">Reason for this update (required)</label>
      <textarea id="visual-update-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3}
        data-testid="visual-update-reason" placeholder="Reason for the update (e.g. Alias QA: approved mobile/desktop hero layout)"
        className="w-full rounded-xl bg-slate-950 ring-1 ring-slate-700 p-3 text-sm text-slate-100 focus:outline-none focus:ring-amber-500" />
      <div className="flex gap-2 justify-end">
        <button onClick={() => setOpen(false)} disabled={busy} data-testid="visual-update-cancel"
          className="rounded-xl px-4 py-2 text-sm text-slate-300 hover:bg-slate-800/60 transition-colors">Cancel</button>
        <button onClick={submit} disabled={!ok || busy} data-testid="visual-update-confirm"
          className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50 transition-colors">
          {busy ? "Creating…" : "Create new draft"}
        </button>
      </div>
    </section>
  );
}
